import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiArrowLeft,
  FiBarChart2,
  FiClock,
  FiTrendingUp,
  FiActivity,
  FiDollarSign,
  FiPercent,
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiLock
} from 'react-icons/fi';
import { getTraderDetail, createShareLink, getTraderInsight, getTraderActivePositions, getTraderActivity } from '../utils/api';
import { getTradingStyleBadgeClass } from '../utils/tradingStyle';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { RovTooltip } from '../components/RovTooltip';
import { CustomNameModal } from '../components/CustomNameModal';
import { CopyTradeConfigModal } from '../components/CopyTradeConfigModal';
import { useI18n } from '../i18n/useI18n';
import { useAuth } from '../contexts/AuthContext';

const COLOR_PALETTE = [
  '#38bdf8', // sky-400
  '#34d399', // emerald-400
  '#fbbf24', // amber-400
  '#f472b6', // pink-400
  '#a78bfa', // violet-400
  '#f87171', // red-400
  '#2dd4bf', // teal-400
  '#c084fc'  // purple-400
];

const formatUSD = (value) => {
  if (value === null || value === undefined) return '$0';
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};


const formatActivityTime = (timestamp, locale) => {
  if (!timestamp) return '--';
  const ts = typeof timestamp === 'string' ? Date.parse(timestamp) : Number(timestamp) * 1000;
  if (!Number.isFinite(ts)) return '--';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString(locale || undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatActivityValue = (value, digits = 2) => {
  if (value === null || value === undefined) return '--';
  const num = Number(value);
  if (!Number.isFinite(num)) return '--';
  return num.toFixed(digits);
};

const getActivityTypeBadgeClass = (type) => {
  switch ((type || '').toUpperCase()) {
    case 'TRADE':
      return 'bg-sky-500/10 text-sky-300 border-sky-500/30';
    case 'REDEEM':
      return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30';
    case 'MERGE':
      return 'bg-violet-500/10 text-violet-300 border-violet-500/30';
    case 'SPLIT':
      return 'bg-amber-500/10 text-amber-300 border-amber-500/30';
    case 'REWARD':
      return 'bg-rose-500/10 text-rose-300 border-rose-500/30';
    case 'CONVERSION':
      return 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30';
    default:
      return 'bg-slate-800/60 text-slate-200 border-slate-600/40';
  }
};

const getActivitySideBadgeClass = (side) => {
  switch ((side || '').toUpperCase()) {
    case 'BUY':
      return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30';
    case 'SELL':
      return 'bg-rose-500/10 text-rose-300 border-rose-500/30';
    default:
      return 'bg-slate-800/60 text-slate-200 border-slate-600/40';
  }
};

const HISTORY_PAGE_SIZE = 100;
const ACTIVITY_PAGE_SIZE = 100;

export function TraderDetail({
  address,
  onBack,
  pushToast = () => { },
  isAuthenticated = false,
  onRequireLogin = () => { },
  onRequireMembership = () => { },
  onBuyPoints = () => { }
}) {
  const { t, lang } = useI18n();
  const { refreshPoints, points, pointsLoading } = useAuth();
  const [detail, setDetail] = useState(null);
  const [isPositionsExpanded, setIsPositionsExpanded] = useState(false);
  const [activePositions, setActivePositions] = useState([]);
  const [positionsLoaded, setPositionsLoaded] = useState(false);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [isActivityExpanded, setIsActivityExpanded] = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);
  const [insightError, setInsightError] = useState('');
  const [customNameOpen, setCustomNameOpen] = useState(false);
  const [membershipRequired, setMembershipRequired] = useState(false);
  const [pointsBlocked, setPointsBlocked] = useState(false);
  const [pointsMessage, setPointsMessage] = useState('');
  const lastPointsRetryBalanceRef = useRef(null);
  const [botRiskState, setBotRiskState] = useState({ open: false, type: '' });
  const [copyConfigOpen, setCopyConfigOpen] = useState(false);
  const [copyConfigTrader, setCopyConfigTrader] = useState(null);
  const leaderboardTag = import.meta.env.VITE_LEADERBOARD_TAG || 'nba';

  const isPointsInsufficient = (message) => /点数不足|insufficient points/i.test(message || '');

  const markPointsBlocked = useCallback(
    (message) => {
      if (!message || membershipRequired || !isPointsInsufficient(message)) return false;
      setPointsBlocked(true);
      setPointsMessage(message);
      setError('');
      return true;
    },
    [membershipRequired]
  );

  const fetchDetail = useCallback(async () => {
    if (!address) return;
    setLoading(true);
    setMembershipRequired(false);
    setPointsBlocked(false);
    setPointsMessage('');
    try {
      const response = await getTraderDetail(address, {
        historyPage: 1,
        historyPageSize: HISTORY_PAGE_SIZE,
        tag: leaderboardTag
      });
      if (response?.code === 0) {
        const payload = response.data;
        setDetail(payload);
        setError('');
        refreshPoints?.();
      } else {
        const message = response?.msg || t('traderDetail.loadFailed');
        if (markPointsBlocked(message)) return;
        setError(message);
      }
    } catch (err) {
      console.error(err);
      if (err?.isMembershipRequired) {
        setMembershipRequired(true);
        setError('');
      } else {
        const message = err?.message || t('traderDetail.loadDetailFailed');
        if (markPointsBlocked(message)) return;
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [address, leaderboardTag, markPointsBlocked, t]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    setIsPositionsExpanded(false);
    setActivePositions([]);
    setPositionsLoaded(false);
    setPositionsLoading(false);
    setIsActivityExpanded(false);
    setActivityItems([]);
    setActivityPage(1);
    setActivityLoaded(false);
    setActivityLoading(false);
    setActivityHasMore(false);
    setPointsBlocked(false);
    setPointsMessage('');
    lastPointsRetryBalanceRef.current = null;
  }, [address, leaderboardTag]);

  const fetchActivePositions = useCallback(async () => {
    if (!address) return;
    setPositionsLoading(true);
    try {
      const response = await getTraderActivePositions(address, leaderboardTag);
      if (response?.code === 0) {
        setActivePositions(response.data?.items || []);
        setPositionsLoaded(true);
      } else if (markPointsBlocked(response?.msg)) {
        return;
      }
    } catch (err) {
      console.error('Failed to fetch active positions:', err);
      if (err?.isMembershipRequired) {
        setMembershipRequired(true);
      } else {
        markPointsBlocked(err?.message);
      }
    } finally {
      setPositionsLoading(false);
    }
  }, [address, leaderboardTag, markPointsBlocked]);

  const fetchActivity = useCallback(async (page = 1, append = false) => {
    if (!address) return;
    setActivityLoading(true);
    try {
      const response = await getTraderActivity(address, {
        page,
        pageSize: ACTIVITY_PAGE_SIZE,
        tag: leaderboardTag
      });
      if (response?.code === 0) {
        const items = response.data?.items || [];
        setActivityItems((prev) => (append ? [...prev, ...items] : items));
        setActivityPage(page);
        setActivityHasMore(Boolean(response.data?.has_more));
        setActivityLoaded(true);
      } else if (markPointsBlocked(response?.msg)) {
        return;
      }
    } catch (err) {
      console.error('Failed to fetch activity records:', err);
      if (err?.isMembershipRequired) {
        setMembershipRequired(true);
      } else {
        markPointsBlocked(err?.message);
      }
    } finally {
      setActivityLoading(false);
    }
  }, [address, leaderboardTag, markPointsBlocked]);

  const fetchInsight = useCallback(async () => {
    if (!address) return;
    try {
      setInsightLoading(true);
      setInsightError('');
      const response = await getTraderInsight(address, lang);
      if (response?.code === 0) {
        setInsight(response.data);
      } else {
        if (!markPointsBlocked(response?.msg)) {
          setInsightError(response?.msg || t('traderDetail.aiAnalysisError'));
        }
      }
    } catch (err) {
      console.error(err);
      if (err?.isMembershipRequired) {
        setMembershipRequired(true);
        setInsightError('');
      } else {
        if (!markPointsBlocked(err?.message)) {
          setInsightError(t('traderDetail.aiAnalysisError'));
        }
      }
    } finally {
      setInsightLoading(false);
    }
  }, [address, lang, markPointsBlocked, t]);

  useEffect(() => {
    fetchInsight();
  }, [fetchInsight]);

  useEffect(() => {
    const handleMembershipUpdated = () => {
      setMembershipRequired(false);
      setPointsBlocked(false);
      setPointsMessage('');
      fetchDetail();
      fetchInsight();
    };
    window.addEventListener('membership-updated', handleMembershipUpdated);
    return () => {
      window.removeEventListener('membership-updated', handleMembershipUpdated);
    };
  }, [fetchDetail, fetchInsight]);

  useEffect(() => {
    const handlePointsUpdated = () => {
      if (!pointsBlocked) return;
      setPointsBlocked(false);
      setPointsMessage('');
      fetchDetail();
      fetchInsight();
    };
    window.addEventListener('points-updated', handlePointsUpdated);
    return () => {
      window.removeEventListener('points-updated', handlePointsUpdated);
    };
  }, [fetchDetail, fetchInsight, pointsBlocked]);

  useEffect(() => {
    if (!pointsBlocked || pointsLoading) return;
    const balance = points?.balance;
    if (balance == null) return;
    if (lastPointsRetryBalanceRef.current === balance) return;
    lastPointsRetryBalanceRef.current = balance;
    fetchDetail();
    fetchInsight();
  }, [pointsBlocked, pointsLoading, points?.balance, fetchDetail, fetchInsight]);

  const riskMetrics = detail?.performance?.risk_metrics || {};
  const stats = detail?.stats || {};
  const profile = detail?.profile || {};
  const summary = stats.history_summary || {};
  const last30d = stats.last_30d || {};
  const hasLast30d = Object.values(last30d).some((value) => value !== null && value !== undefined);
  const domainLabels = detail?.domain_labels || {};
  const displayName = profile.custom_name || profile.username || t('traderDetail.anonymousTrader');
  const secondaryName = profile.custom_name ? profile.username : null;
  const tradingStyle = insight?.trading_style || '';
  const rawSuspiciousType = insight?.suspicious_type || 'none';
  const normalizedSuspiciousType = rawSuspiciousType === 'market-maker' ? 'market_maker' : rawSuspiciousType;
  const isAiSuspicious = ['robot', 'market_maker'].includes(normalizedSuspiciousType);
  const aiScoreValue = Number(insight?.ai_score ?? insight?.copy_trading_score ?? 0);
  const aiSummary = insight?.strategy_summary || insight?.summary || '-';
  const aiScoreReason = insight?.score_reason || insight?.recommendation?.rationale || '-';
  const aiAnalysis = insight?.analysis || {};

  const suspiciousLabelMap = {
    robot: t('traderDetail.aiSuspectedRobot'),
    market_maker: t('traderDetail.aiSuspectedMarketMaker'),
  };
  const suspiciousLabel = suspiciousLabelMap[normalizedSuspiciousType];
  const tradingStyleBadgeClass = getTradingStyleBadgeClass(tradingStyle);

  const displayWinRate = toNumber(hasLast30d ? last30d.win_rate : stats.win_rate);
  const displayRov = toNumber(hasLast30d ? last30d.rov : stats.rov);
  const displayPnl = toNumber(hasLast30d ? last30d.total_profit : (stats.total_pnl ?? stats.total_profit));
  const displayVolume = toNumber(
    hasLast30d ? last30d.total_volume : (stats.total_volume ?? summary.total_invested)
  );

  const insightReady = Boolean(insight) && !insightLoading && !insightError;
  const positionsCount = positionsLoaded ? activePositions.length : profile.active_positions;
  const hasPositionsCount = Number.isFinite(positionsCount);
  const positionsCountLabel = hasPositionsCount ? positionsCount : 0;
  const activityLocale = lang === 'zh' ? 'zh-CN' : 'en-US';
  const activityTypeLabelMap = {
    TRADE: t('traderDetail.activityTypeTrade'),
    REDEEM: t('traderDetail.activityTypeRedeem'),
    MERGE: t('traderDetail.activityTypeMerge'),
    SPLIT: t('traderDetail.activityTypeSplit'),
    REWARD: t('traderDetail.activityTypeReward'),
    CONVERSION: t('traderDetail.activityTypeConversion')
  };
  const activitySideLabelMap = {
    BUY: t('traderDetail.activitySideBuy'),
    SELL: t('traderDetail.activitySideSell')
  };

  const handleOpenCustomName = () => {
    if (!isAuthenticated) {
      pushToast(t('customName.loginRequired'), 'info');
      onRequireLogin();
      return;
    }
    setCustomNameOpen(true);
  };

  const handleCustomNameSaved = (customName) => {
    setDetail((prev) => {
      if (!prev?.profile) return prev;
      return {
        ...prev,
        profile: {
          ...prev.profile,
          custom_name: customName || null
        }
      };
    });
  };

  const handleTogglePositions = () => {
    setIsPositionsExpanded((prev) => {
      const next = !prev;
      if (next && !positionsLoaded && !positionsLoading) {
        fetchActivePositions();
      }
      return next;
    });
  };

  const handleToggleActivity = () => {
    setIsActivityExpanded((prev) => {
      const next = !prev;
      if (next && !activityLoaded && !activityLoading) {
        fetchActivity(1, false);
      }
      return next;
    });
  };

  const handleLoadMoreActivity = () => {
    if (activityLoading || !activityHasMore) return;
    fetchActivity(activityPage + 1, true);
  };

  const openBotRiskModal = useCallback((typeLabel) => {
    setBotRiskState({ open: true, type: typeLabel || '' });
  }, []);

  const closeBotRiskModal = useCallback(() => {
    setBotRiskState({ open: false, type: '' });
  }, []);

  const openCopyConfigModal = useCallback(() => {
    if (!address) return;
    setCopyConfigTrader({
      address,
      name: displayName,
      secondaryName,
      winRate: displayWinRate,
      totalProfit: displayPnl,
      aiScore: aiScoreValue,
      tradingStyle,
    });
    setCopyConfigOpen(true);
  }, [address, displayName, secondaryName, displayWinRate, displayPnl, aiScoreValue, tradingStyle]);

  const closeCopyConfigModal = useCallback(() => {
    setCopyConfigOpen(false);
  }, []);

  const handleBotRiskConfirm = useCallback(() => {
    closeBotRiskModal();
    openCopyConfigModal();
  }, [closeBotRiskModal, openCopyConfigModal]);

  if (loading) {
    return (
      <section className="glass-lg p-6 md:p-10 w-full animate-pulse min-h-[60vh]">
        <div className="h-8 w-1/3 bg-slate-800/50 rounded-lg mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="h-32 bg-slate-800/50 rounded-2xl" />
          <div className="h-32 bg-slate-800/50 rounded-2xl" />
          <div className="h-32 bg-slate-800/50 rounded-2xl" />
        </div>
        <div className="h-64 bg-slate-800/50 rounded-2xl" />
      </section>
    );
  }

  if (pointsBlocked) {
    const pointsBalance = Number(points?.balance ?? 0);
    return (
      <section className="glass-lg w-full overflow-hidden min-h-[60vh] flex items-center justify-center">
        <div className="relative w-full max-w-3xl px-6 py-10 md:px-10 md:py-12 text-center">
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-500/10 blur-[140px]" />
          <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-sky-500/10 blur-[140px]" />

          <div className="relative z-10 flex flex-col items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-400/30 bg-amber-500/12 shadow-[0_0_30px_rgba(245,158,11,0.18)]">
              <FiLock className="h-6 w-6 text-amber-300" />
            </div>

            <h2 className="text-2xl md:text-3xl font-semibold text-white">
              {t('traderDetail.pointsInsufficientTitle')}
            </h2>

            <p className="max-w-xl text-sm md:text-base text-slate-300/90 leading-relaxed">
              {t('traderDetail.pointsInsufficientDesc')}
            </p>

            <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-950/30 px-3 py-1 text-[11px] text-slate-300">
                <span className="text-slate-500">{t('traderDetail.pointsBalanceLabel')}</span>
                <span className="font-mono text-slate-100">
                  {pointsLoading ? '--' : pointsBalance.toLocaleString()}
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-500">
              {t('traderDetail.pointsInsufficientHint')}
            </p>

            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              {onBuyPoints && (
                <Button variant="primary" onClick={() => onBuyPoints?.()}>
                  {t('points.buy')}
                </Button>
              )}
              <Button variant={onBuyPoints ? 'secondary' : 'primary'} onClick={() => onRequireMembership?.()}>
                {t('membership.viewPlans')}
              </Button>
              <Button variant="secondary" onClick={onBack}>
                {t('traderDetail.backToPrevious')}
              </Button>
            </div>

            {pointsMessage && (
              <div className="mt-3 rounded-full border border-slate-700/60 bg-slate-950/30 px-3 py-1 text-[11px] text-slate-500">
                {pointsMessage}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  if (membershipRequired) {
    return (
      <section className="glass-lg p-6 md:p-10 w-full min-h-[40vh] flex flex-col items-center justify-center text-center">
        <div className="text-amber-300 text-lg font-semibold mb-2">{t('membership.upgradeRequired')}</div>
        <p className="text-slate-400 mb-6">{t('traderDetail.membershipRequiredDesc')}</p>
        <div className="flex items-center gap-3">
          <Button variant="primary" onClick={() => onRequireMembership?.()}>{t('membership.viewPlans')}</Button>
          <Button variant="secondary" onClick={fetchDetail}>{t('common.retry')}</Button>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="glass-lg p-6 md:p-10 w-full min-h-[40vh] flex flex-col items-center justify-center text-center">
        <div className="text-rose-400 text-lg font-semibold mb-2">{t('traderDetail.loadFailed')}</div>
        <p className="text-slate-400 mb-6">{error}</p>
        <Button onClick={fetchDetail}>{t('common.retry')}</Button>
      </section>
    );
  }

  return (
    <div className="glass-lg w-full p-6 md:p-8 lg:p-10 flex flex-col gap-8 text-text relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 rounded-full bg-sky-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-24 h-72 w-72 rounded-full bg-violet-500/10 blur-[120px]" />
      <div className="relative z-10 flex flex-col gap-8">
        {/* Header - Compact */}
        <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-slate-900/70 via-slate-900/40 to-slate-950/60 p-4 md:p-5 shadow-[0_12px_40px_rgba(2,6,23,0.35)] relative overflow-hidden">
          <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-sky-500/8 blur-[60px]" />
          <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-indigo-500/8 blur-[60px]" />
          <div className="relative z-10 flex flex-col gap-3">
            <button
              type="button"
              className="group inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors w-fit"
              onClick={onBack}
            >
              <div className="p-1 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors">
                <FiArrowLeft className="w-3 h-3" />
              </div>
              {t('traderDetail.backToPrevious')}
            </button>

            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl md:text-2xl font-bold text-white">
                    {displayName}
                  </h1>
                  {secondaryName && (
                    <span className="text-xs text-slate-400">({secondaryName})</span>
                  )}
                  <button
                    type="button"
                    className="p-1 rounded-full bg-slate-800/70 border border-slate-700/60 text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
                    onClick={handleOpenCustomName}
                    title={t('customName.edit')}
                    aria-label={t('customName.edit')}
                  >
                    <FiEdit2 className="text-xs" />
                  </button>
                  <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-400">
                    {address.slice(0, 6)}...{address.slice(-4)}
                  </span>
                  {/* Trader domain tags */}
                  {profile.domains?.slice(0, 3).map((domain, index) => {
                    const displayName = lang === 'zh' ? (domainLabels[domain] || domain) : domain;
                    return (
                      <span
                        key={domain}
                        className="px-1.5 py-0.5 rounded-full text-[10px] font-medium border"
                        style={{
                          backgroundColor: `${COLOR_PALETTE[index % COLOR_PALETTE.length]}15`,
                          borderColor: `${COLOR_PALETTE[index % COLOR_PALETTE.length]}40`,
                          color: COLOR_PALETTE[index % COLOR_PALETTE.length]
                        }}
                      >
                        {displayName}
                      </span>
                    );
                  })}
                  {profile.domains?.length > 3 && (
                    <span className="text-slate-500 text-[10px]">+{profile.domains.length - 3}</span>
                  )}
                </div>
                <p className="text-slate-400 text-xs max-w-xl">
                  {t('traderDetail.traderDescription')}
                </p>
              </div>

              <div className="flex gap-2 shrink-0">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={async () => {
                    if (!isAuthenticated) {
                      pushToast(t('traderDetail.shareNeedLogin'), 'info');
                      onRequireLogin();
                      return;
                    }

                    try {
                      const traderUrl = `${window.location.origin}/traders/${address}`;
                      const response = await createShareLink({
                        share_type: 'trader',
                        target_page: `/traders/${address}`,
                        target_params: { address }
                      });

                      if (response?.code === 0 && response?.data?.share_url) {
                        const fullShareUrl = `${window.location.origin}${response.data.share_url}`;
                        await navigator.clipboard.writeText(fullShareUrl);
                        pushToast(t('traderDetail.shareCopied'), 'success');
                      } else {
                        await navigator.clipboard.writeText(traderUrl);
                        pushToast(t('traderDetail.shareCopied'), 'success');
                      }
                    } catch (error) {
                      console.error('[Share error]', error);
                      pushToast(t('traderDetail.shareUnavailable'), 'info');
                    }
                  }}
                >
                  {t('traderDetail.share')}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={async () => {
                    if (!isAuthenticated) {
                      pushToast(t('traderDetail.copyNeedLogin'), 'info');
                      onRequireLogin();
                      return;
                    }
                    if (isAiSuspicious && suspiciousLabel) {
                      openBotRiskModal(suspiciousLabel);
                      return;
                    }
                    openCopyConfigModal();
                  }}
                >
                  {t('traderDetail.copyTrade')}
                </Button>
              </div>
            </div>
          </div>
        </div>

      {/* AI Analysis - Enhanced */}
      <div className="relative overflow-hidden rounded-3xl border border-sky-500/30 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-slate-950/95 p-6 md:p-8 shadow-[0_12px_60px_rgba(56,189,248,0.15),inset_0_1px_0_rgba(255,255,255,0.08)]">
        {/* Enhanced ambient glow effects */}
        <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-sky-500/12 blur-[120px]" />
        <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-violet-500/12 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-indigo-500/8 blur-[140px]" />
        {/* Top gradient line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-500/40 to-transparent" />

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500/30 to-indigo-500/30 border border-sky-500/30 shadow-[0_0_30px_rgba(56,189,248,0.25)]">
              <FiTrendingUp className="w-6 h-6 text-sky-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{t('traderDetail.aiAnalysisTitle')}</h2>
              <p className="text-xs text-sky-400/80 uppercase tracking-wider font-medium">{t('traderDetail.aiAnalysisSubtitle')}</p>
            </div>
          </div>
          {/* Trading Style & Suspicion Tags */}
          {insight && (
            <div className="flex items-center gap-2">
              {tradingStyle && (
                <span className={`px-3 py-1.5 rounded-lg text-xs font-medium border backdrop-blur-sm shadow-[0_4px_15px_rgba(0,0,0,0.2)] ${tradingStyleBadgeClass}`}>
                  {tradingStyle}
                </span>
              )}
              {isAiSuspicious && suspiciousLabel && (
                <span className="px-3 py-1.5 rounded-lg text-xs font-medium border backdrop-blur-sm bg-amber-500/15 text-amber-300 border-amber-500/40 shadow-[0_4px_15px_rgba(245,158,11,0.2)]">
                  {suspiciousLabel}
                </span>
              )}
            </div>
          )}
        </div>

        {insightLoading ? (
          <div className="relative z-10 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-32 bg-slate-800/30 rounded-2xl animate-pulse" />
              <div className="h-32 bg-slate-800/30 rounded-2xl animate-pulse" />
              <div className="h-32 bg-slate-800/30 rounded-2xl animate-pulse" />
            </div>
            <p className="text-xs text-slate-500 text-center">{t('traderDetail.aiAnalysisLoading')}</p>
          </div>
        ) : insightError ? (
          <div className="relative z-10 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-2xl p-5">
            {insightError}
          </div>
        ) : insight ? (
          <div className="relative z-10 space-y-6">
            {/* Top Section: Score & Summary */}
            <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-6">
              {/* Left: AI Score Card */}
              <div className="relative overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/60 via-slate-900/60 to-slate-950/60 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                <div className="absolute top-0 right-0 h-32 w-32 rounded-full bg-sky-500/10 blur-3xl" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.6)]" />
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{t('traderDetail.aiScore')}</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-5xl font-bold bg-gradient-to-r from-sky-300 to-indigo-300 bg-clip-text text-transparent">
                      {aiScoreValue.toFixed(0)}
                    </span>
                    <span className="text-sm text-slate-500 font-medium">/ 100</span>
                  </div>
                  {/* Score Progress Bar */}
                  <div className="relative h-2.5 rounded-full bg-slate-800/80 overflow-hidden">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-sky-500/20 via-indigo-500/20 to-violet-500/20" />
                    <div
                      className="relative h-full rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-violet-500 shadow-[0_0_10px_rgba(56,189,248,0.5)] transition-all duration-1000 ease-out"
                      style={{ width: `${Math.min(100, Math.max(0, aiScoreValue))}%` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                    </div>
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-slate-600">
                    <span>0</span>
                    <span>50</span>
                    <span>100</span>
                  </div>
                </div>
              </div>

              {/* Right: Strategy Summary & Score Reason */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Strategy Summary */}
                <div className="relative overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-900/40 p-5">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-emerald-500/50 via-teal-500/30 to-transparent" />
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <FiActivity className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <span className="text-xs font-medium text-slate-300">{t('traderDetail.aiStrategySummary')}</span>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{aiSummary || '-'}</p>
                </div>

                {/* Score Reason */}
                <div className="relative overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-900/40 p-5">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-violet-500/50 via-purple-500/30 to-transparent" />
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-lg bg-violet-500/10 border border-violet-500/20">
                      <FiBarChart2 className="w-3.5 h-3.5 text-violet-400" />
                    </div>
                    <span className="text-xs font-medium text-slate-300">{t('traderDetail.aiScoreReason')}</span>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{aiScoreReason || '-'}</p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="relative h-px">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />
            </div>

            {/* Analysis Sections */}
            <div className="space-y-5">
              {/* Execution Logic Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-sky-400 to-indigo-400" />
                  <h3 className="text-sm font-semibold text-slate-200">{t('traderDetail.aiSectionExecution')}</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-slate-700/50 to-transparent ml-3" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Entry Exit */}
                  <div className="group relative overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 transition-all duration-300 hover:border-slate-600/60 hover:bg-slate-800/30 hover:shadow-[0_4px_20px_rgba(2,6,23,0.4)]">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-sky-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                      <span className="text-xs font-medium text-slate-400">{t('traderDetail.aiEntryExit')}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {aiAnalysis?.entry_exit || '-'}
                    </p>
                  </div>
                  {/* Price Sensitivity */}
                  <div className="group relative overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 transition-all duration-300 hover:border-slate-600/60 hover:bg-slate-800/30 hover:shadow-[0_4px_20px_rgba(2,6,23,0.4)]">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-indigo-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                      <span className="text-xs font-medium text-slate-400">{t('traderDetail.aiPriceSensitivity')}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {aiAnalysis?.price_sensitivity || '-'}
                    </p>
                  </div>
                  {/* Take Profit */}
                  <div className="group relative overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 transition-all duration-300 hover:border-slate-600/60 hover:bg-slate-800/30 hover:shadow-[0_4px_20px_rgba(2,6,23,0.4)]">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-violet-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                      <span className="text-xs font-medium text-slate-400">{t('traderDetail.aiTakeProfit')}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {aiAnalysis?.take_profit || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Risk Control Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-amber-400 to-orange-400" />
                  <h3 className="text-sm font-semibold text-slate-200">{t('traderDetail.aiSectionRisk')}</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-slate-700/50 to-transparent ml-3" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Risk Control */}
                  <div className="group relative overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 transition-all duration-300 hover:border-slate-600/60 hover:bg-slate-800/30 hover:shadow-[0_4px_20px_rgba(2,6,23,0.4)]">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      <span className="text-xs font-medium text-slate-400">{t('traderDetail.aiRiskControl')}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {aiAnalysis?.risk_control || '-'}
                    </p>
                  </div>
                  {/* Hedging */}
                  <div className="group relative overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 transition-all duration-300 hover:border-slate-600/60 hover:bg-slate-800/30 hover:shadow-[0_4px_20px_rgba(2,6,23,0.4)]">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                      <span className="text-xs font-medium text-slate-400">{t('traderDetail.aiHedging')}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {aiAnalysis?.hedging || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Behavior Pattern Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-emerald-400 to-teal-400" />
                  <h3 className="text-sm font-semibold text-slate-200">{t('traderDetail.aiSectionBehavior')}</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-slate-700/50 to-transparent ml-3" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* News Trading */}
                  <div className="group relative overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 transition-all duration-300 hover:border-slate-600/60 hover:bg-slate-800/30 hover:shadow-[0_4px_20px_rgba(2,6,23,0.4)]">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-emerald-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <span className="text-xs font-medium text-slate-400">{t('traderDetail.aiNewsTrading')}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {aiAnalysis?.news_trading || '-'}
                    </p>
                  </div>
                  {/* Frequency */}
                  <div className="group relative overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 transition-all duration-300 hover:border-slate-600/60 hover:bg-slate-800/30 hover:shadow-[0_4px_20px_rgba(2,6,23,0.4)]">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-teal-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                      <span className="text-xs font-medium text-slate-400">{t('traderDetail.aiFrequency')}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {aiAnalysis?.frequency || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Win Rate Card */}
        <div className="group relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/30 via-slate-900/60 to-slate-950/60 p-5 shadow-[0_8px_30px_rgba(2,6,23,0.4),inset_0_1px_0_rgba(255,255,255,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(16,185,129,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] hover:border-emerald-500/40">
          <div className="absolute top-0 right-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full bg-emerald-500/20 blur-3xl group-hover:bg-emerald-500/30 transition-colors" />
          <div className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-emerald-500/10 blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/25 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                  <FiActivity className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-400/80">{t('traderDetail.winRate')}</span>
              </div>
              {hasLast30d && (
                <span className="px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-300/80">
                  {t('traderDetail.last30dBadge')}
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold bg-gradient-to-r from-emerald-300 to-emerald-400 bg-clip-text text-transparent">
                {displayWinRate.toFixed(1)}
              </span>
              <span className="text-lg font-medium text-emerald-400/60">%</span>
            </div>
            {/* Mini progress bar */}
            <div className="mt-4 h-1.5 rounded-full bg-slate-800/80 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                style={{ width: `${Math.min(100, Math.max(0, displayWinRate))}%` }}
              />
            </div>
          </div>
        </div>

        {/* ROV Card */}
        <div className="group relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-950/30 via-slate-900/60 to-slate-950/60 p-5 shadow-[0_8px_30px_rgba(2,6,23,0.4),inset_0_1px_0_rgba(255,255,255,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(139,92,246,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] hover:border-violet-500/40">
          <div className="absolute top-0 right-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full bg-violet-500/20 blur-3xl group-hover:bg-violet-500/30 transition-colors" />
          <div className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-violet-500/10 blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-500/15 border border-violet-500/25 shadow-[0_0_15px_rgba(139,92,246,0.2)]">
                  <FiPercent className="w-4 h-4 text-violet-400" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-violet-400/80">ROV</span>
              </div>
              {hasLast30d && (
                <span className="px-2 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-[10px] text-violet-300/80">
                  {t('traderDetail.last30dBadge')}
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-1">
              <span className={`text-4xl font-bold bg-gradient-to-r ${displayRov >= 0 ? 'from-violet-300 to-violet-400' : 'from-rose-300 to-rose-400'} bg-clip-text text-transparent`}>
                {displayRov >= 0 ? '+' : ''}{displayRov.toFixed(1)}
              </span>
              <span className={`text-lg font-medium ${displayRov >= 0 ? 'text-violet-400/60' : 'text-rose-400/60'}`}>%</span>
            </div>
            {/* Trend indicator */}
            <div className="mt-4 flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${displayRov >= 0 ? 'bg-violet-400 shadow-[0_0_6px_rgba(139,92,246,0.8)]' : 'bg-rose-400 shadow-[0_0_6px_rgba(244,63,94,0.8)]'}`} />
              <span className={`text-[10px] ${displayRov >= 0 ? 'text-violet-400/60' : 'text-rose-400/60'}`}>
                {displayRov >= 0 ? 'Positive Return' : 'Negative Return'}
              </span>
            </div>
          </div>
        </div>

        {/* Total PnL Card */}
        <div className="group relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-950/30 via-slate-900/60 to-slate-950/60 p-5 shadow-[0_8px_30px_rgba(2,6,23,0.4),inset_0_1px_0_rgba(255,255,255,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(245,158,11,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] hover:border-amber-500/40">
          <div className="absolute top-0 right-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full bg-amber-500/20 blur-3xl group-hover:bg-amber-500/30 transition-colors" />
          <div className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-amber-500/10 blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/25 shadow-[0_0_15px_rgba(245,158,11,0.2)]">
                  <FiDollarSign className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-400/80">{t('traderDetail.totalPnl')}</span>
              </div>
              {hasLast30d && (
                <span className="px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-[10px] text-amber-300/80">
                  {t('traderDetail.last30dBadge')}
                </span>
              )}
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold bg-gradient-to-r from-amber-300 to-amber-400 bg-clip-text text-transparent">
                {formatUSD(displayPnl)}
              </span>
            </div>
            {/* PnL indicator */}
            <div className="mt-4 flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-slate-800/80 overflow-hidden">
                <div
                  className={`h-full rounded-full ${displayPnl >= 0 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 'bg-gradient-to-r from-rose-500 to-rose-400'}`}
                  style={{ width: `${Math.min(100, Math.max(0, Math.abs(displayPnl) / 10000 * 100))}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Total Volume Card */}
        <div className="group relative overflow-hidden rounded-2xl border border-sky-500/20 bg-gradient-to-br from-sky-950/30 via-slate-900/60 to-slate-950/60 p-5 shadow-[0_8px_30px_rgba(2,6,23,0.4),inset_0_1px_0_rgba(255,255,255,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(56,189,248,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] hover:border-sky-500/40">
          <div className="absolute top-0 right-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full bg-sky-500/20 blur-3xl group-hover:bg-sky-500/30 transition-colors" />
          <div className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-sky-500/10 blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-500/25 shadow-[0_0_15px_rgba(56,189,248,0.2)]">
                  <FiTrendingUp className="w-4 h-4 text-sky-400" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-sky-400/80">{t('traderDetail.totalVolume')}</span>
              </div>
              {hasLast30d && (
                <span className="px-2 py-0.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-[10px] text-sky-300/80">
                  {t('traderDetail.last30dBadge')}
                </span>
              )}
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold bg-gradient-to-r from-sky-300 to-sky-400 bg-clip-text text-transparent">
                {formatUSD(displayVolume)}
              </span>
            </div>
            {/* Volume indicator */}
            <div className="mt-4 flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-sky-400 shadow-[0_0_6px_rgba(56,189,248,0.8)]" />
              <span className="text-[10px] text-sky-400/60">Total Trading Volume</span>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Metrics - Horizontal Grid */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-700/40 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-slate-950/90 p-6 md:p-8 shadow-[0_8px_40px_rgba(2,6,23,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]">
        {/* Ambient glow effects */}
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-rose-500/5 blur-[100px]" />
        <div className="absolute -bottom-16 -left-12 h-48 w-48 rounded-full bg-emerald-500/5 blur-[80px]" />

        {/* Header */}
        <div className="relative z-10 flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 to-orange-500/20 border border-rose-500/20 shadow-[0_0_20px_rgba(244,63,94,0.15)]">
            <FiActivity className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">{t('traderDetail.riskMetrics')}</h2>
            <p className="text-[11px] text-slate-500 uppercase tracking-wider">Risk Analysis</p>
          </div>
        </div>

        {/* Risk Cards Grid */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Volatility Card */}
          <div className="group relative overflow-hidden rounded-2xl border border-slate-600/30 bg-slate-950/40 p-5 transition-all duration-300 hover:border-slate-500/50 hover:bg-slate-900/50 hover:shadow-[0_8px_30px_rgba(2,6,23,0.4)]">
            <div className="absolute top-0 right-0 h-24 w-24 translate-x-6 -translate-y-6 rounded-full bg-slate-500/10 blur-2xl group-hover:bg-slate-500/15 transition-colors" />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-500/15 border border-slate-500/25">
                    <FiActivity className="w-4 h-4 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-300">{t('traderDetail.volatility')}</p>
                    <p className="text-[10px] text-slate-500">Price fluctuation</p>
                  </div>
                </div>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-bold text-slate-200">
                  {(riskMetrics.volatility || 0).toFixed(2)}
                </span>
                <span className="text-sm text-slate-500">%</span>
              </div>
              {/* Progress bar with dynamic width based on value */}
              <div className="h-2 rounded-full bg-slate-800/80 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-slate-500 to-slate-400 shadow-[0_0_8px_rgba(148,163,184,0.3)]"
                  style={{ width: `${Math.min(100, (riskMetrics.volatility || 0) * 2)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-slate-600">
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
          </div>

          {/* Max Drawdown Card */}
          <div className="group relative overflow-hidden rounded-2xl border border-rose-500/20 bg-gradient-to-br from-rose-950/20 via-slate-950/40 to-slate-950/40 p-5 transition-all duration-300 hover:border-rose-500/40 hover:shadow-[0_8px_30px_rgba(244,63,94,0.15)]">
            <div className="absolute top-0 right-0 h-24 w-24 translate-x-6 -translate-y-6 rounded-full bg-rose-500/15 blur-2xl group-hover:bg-rose-500/25 transition-colors" />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-rose-500/15 border border-rose-500/25 shadow-[0_0_15px_rgba(244,63,94,0.15)]">
                    <FiTrendingUp className="w-4 h-4 text-rose-400 rotate-180" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-rose-300">{t('traderDetail.maxDrawdown')}</p>
                    <p className="text-[10px] text-rose-400/60">Peak to trough decline</p>
                  </div>
                </div>
              </div>
              <div className="flex items-baseline mb-4">
                <span className="text-3xl font-bold bg-gradient-to-r from-rose-300 to-rose-400 bg-clip-text text-transparent">
                  {formatUSD(riskMetrics.max_drawdown || 0)}
                </span>
              </div>
              {/* Progress bar */}
              <div className="h-2 rounded-full bg-slate-800/80 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-rose-500 to-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.4)]"
                  style={{ width: `${Math.min(100, Math.abs(riskMetrics.max_drawdown || 0) / 1000)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-slate-600">
                <span>Safe</span>
                <span>Risky</span>
              </div>
            </div>
          </div>

          {/* Sharpe Ratio Card */}
          <div className="group relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/20 via-slate-950/40 to-slate-950/40 p-5 transition-all duration-300 hover:border-emerald-500/40 hover:shadow-[0_8px_30px_rgba(16,185,129,0.15)]">
            <div className="absolute top-0 right-0 h-24 w-24 translate-x-6 -translate-y-6 rounded-full bg-emerald-500/15 blur-2xl group-hover:bg-emerald-500/25 transition-colors" />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/25 shadow-[0_0_15px_rgba(16,185,129,0.15)]">
                    <FiBarChart2 className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-emerald-300">{t('traderDetail.sharpeRatio')}</p>
                    <p className="text-[10px] text-emerald-400/60">Risk-adjusted return</p>
                  </div>
                </div>
              </div>
              <div className="flex items-baseline mb-4">
                <span className="text-3xl font-bold bg-gradient-to-r from-emerald-300 to-emerald-400 bg-clip-text text-transparent">
                  {riskMetrics.sharpe_ratio ? riskMetrics.sharpe_ratio.toFixed(2) : '--'}
                </span>
              </div>
              {/* Progress bar */}
              <div className="h-2 rounded-full bg-slate-800/80 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.4)]"
                  style={{ width: `${Math.min(100, Math.max(0, (riskMetrics.sharpe_ratio || 0) / 3 * 100))}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-slate-600">
                <span>&lt; 1</span>
                <span>1-2</span>
                <span>&gt; 2</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Positions */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-700/40 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-slate-950/90 p-6 md:p-8 shadow-[0_8px_40px_rgba(2,6,23,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]">
        {/* Ambient glow effects */}
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-sky-500/5 blur-[100px]" />
        <div className="absolute -bottom-16 -left-12 h-48 w-48 rounded-full bg-indigo-500/5 blur-[80px]" />

        {/* Header */}
        <div
          className="relative z-10 flex items-center justify-between mb-6 cursor-pointer select-none group"
          onClick={handleTogglePositions}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500/20 to-indigo-500/20 border border-sky-500/20 shadow-[0_0_20px_rgba(56,189,248,0.15)]">
              <FiTrendingUp className="w-5 h-5 text-sky-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">{t('traderDetail.currentPositions')}</h2>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">Active Markets</p>
            </div>
            <span className="ml-2 text-slate-500 transition-transform duration-300 group-hover:text-sky-400">
              {isPositionsExpanded ? <FiChevronUp className="w-5 h-5" /> : <FiChevronDown className="w-5 h-5" />}
            </span>
          </div>
          <span className="px-3 py-1.5 rounded-xl bg-sky-500/10 border border-sky-500/30 text-xs font-medium text-sky-300">
            {hasPositionsCount ? positionsCount : '--'} {t('traderDetail.positionsOngoing', { count: positionsCountLabel }).replace(/\d+\s*/, '')}
          </span>
        </div>

        {isPositionsExpanded && (
          positionsLoading ? (
            <div className="relative z-10 flex flex-col items-center justify-center py-12 text-slate-500 gap-3">
              <div className="w-10 h-10 border-2 border-slate-700 border-t-sky-400 rounded-full animate-spin" />
              <p className="text-sm text-slate-400">{t('common.loading')}</p>
            </div>
          ) : activePositions?.length ? (
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {activePositions.map((position, index) => {
                const pnl = (position.current_value || 0) - (position.potential_payout || 0);
                const pnlPercent = position.potential_payout ? (pnl / position.potential_payout) * 100 : 0;
                const isProfitable = pnl >= 0;

                return (
                  <div
                    key={`${position.condition_id}-${position.outcome}`}
                    className="group relative overflow-hidden rounded-2xl border border-slate-700/40 bg-slate-950/40 p-5 transition-all duration-300 hover:border-sky-500/30 hover:bg-slate-900/50 hover:shadow-[0_8px_30px_rgba(2,6,23,0.4)]"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* Top gradient line on hover */}
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-sky-500/60 via-indigo-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                    {/* Market Title */}
                    <h3 className="text-sm font-medium text-slate-200 line-clamp-2 mb-4 group-hover:text-white transition-colors leading-relaxed">
                      {lang === 'zh' ? (position.market_zh || position.market) : (position.market || position.market_zh)}
                    </h3>

                    {/* Outcome & Deadline Row */}
                    <div className="flex items-center justify-between mb-4">
                      <div className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${
                        position.outcome?.toLowerCase() === 'yes'
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                          : position.outcome?.toLowerCase() === 'no'
                          ? 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                          : 'bg-sky-500/10 border-sky-500/30 text-sky-300'
                      }`}>
                        {position.outcome}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <FiClock className="w-3 h-3" />
                        <span>{position.end_date || '—'} {t('traderDetail.deadline')}</span>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Invested */}
                      <div className="relative overflow-hidden rounded-xl bg-slate-900/60 border border-slate-700/30 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('traderDetail.invested')}</div>
                        <div className="font-mono text-sm text-slate-300">{formatUSD(position.potential_payout)}</div>
                      </div>

                      {/* Current Value with PnL */}
                      <div className="relative overflow-hidden rounded-xl bg-slate-900/60 border border-slate-700/30 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('traderDetail.currentValue')}</div>
                        <div className="font-mono text-sm text-white">{formatUSD(position.current_value)}</div>
                        {/* PnL indicator */}
                        <div className={`absolute top-2 right-2 text-[10px] font-medium ${isProfitable ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {isProfitable ? '+' : ''}{pnlPercent.toFixed(1)}%
                        </div>
                      </div>
                    </div>

                    {/* PnL Progress Bar */}
                    <div className="mt-4">
                      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isProfitable
                              ? 'bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.4)]'
                              : 'bg-gradient-to-r from-rose-500 to-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.4)]'
                          }`}
                          style={{ width: `${Math.min(100, Math.max(0, 50 + pnlPercent / 2))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="relative z-10 flex flex-col items-center justify-center py-16 text-slate-500 gap-3">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
                <FiClock className="text-3xl text-slate-600" />
              </div>
              <p className="text-sm text-slate-400">{t('traderDetail.noPositions')}</p>
              <p className="text-xs text-slate-600">No active positions at the moment</p>
            </div>
          )
        )}
      </div>

      {/* Activity */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-700/40 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-slate-950/90 p-6 md:p-8 shadow-[0_8px_40px_rgba(2,6,23,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]">
        {/* Ambient glow effects */}
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-amber-500/5 blur-[100px]" />
        <div className="absolute -bottom-16 -left-12 h-48 w-48 rounded-full bg-rose-500/5 blur-[80px]" />

        {/* Header */}
        <div
          className="relative z-10 flex items-center justify-between mb-6 cursor-pointer select-none group"
          onClick={handleToggleActivity}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-rose-500/20 border border-amber-500/20 shadow-[0_0_20px_rgba(245,158,11,0.15)]">
              <FiActivity className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">{t('traderDetail.activityTitle')}</h2>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">{t('traderDetail.activitySubtitle')}</p>
            </div>
            <span className="ml-2 text-slate-500 transition-transform duration-300 group-hover:text-amber-400">
              {isActivityExpanded ? <FiChevronUp className="w-5 h-5" /> : <FiChevronDown className="w-5 h-5" />}
            </span>
          </div>
          <span className="px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs font-medium text-amber-300">
            {activityLoaded ? t('traderDetail.activityRecords', { count: activityItems.length }) : '--'}
          </span>
        </div>

        {isActivityExpanded && (
          activityLoading && !activityItems.length ? (
            <div className="relative z-10 flex flex-col items-center justify-center py-12 text-slate-500 gap-3">
              <div className="w-10 h-10 border-2 border-slate-700 border-t-amber-400 rounded-full animate-spin" />
              <p className="text-sm text-slate-400">{t('common.loading')}</p>
            </div>
          ) : activityItems.length ? (
            <div className="relative z-10 flex flex-col gap-4">
              {activityItems.map((item) => {
                const type = (item.activity_type || '').toUpperCase();
                const side = (item.side || '').toUpperCase();
                const typeLabel = activityTypeLabelMap[type] || item.activity_type || '--';
                const sideLabel = activitySideLabelMap[side] || item.side;
                const showPrice = type !== 'REDEEM';
                const marketTitle = lang === 'zh'
                  ? (item.market_zh || item.market || '--')
                  : (item.market || item.market_zh || '--');
                const timeLabel = formatActivityTime(item.time || item.timestamp, activityLocale);
                const txHash = item.transaction_hash || '';
                const txLabel = txHash ? `${txHash.slice(0, 6)}...${txHash.slice(-4)}` : '';

                return (
                  <div
                    key={`${item.transaction_hash || item.timestamp}-${item.condition_id}-${item.activity_type}`}
                    className="group relative overflow-hidden rounded-2xl border border-slate-700/40 bg-slate-950/40 p-4 transition-all duration-300 hover:border-amber-500/30 hover:bg-slate-900/50 hover:shadow-[0_8px_30px_rgba(2,6,23,0.4)]"
                  >
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-500/60 via-rose-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-medium text-slate-200 truncate group-hover:text-white transition-colors">
                            {marketTitle}
                          </h3>
                          <span className="text-xs text-slate-500 whitespace-nowrap">{timeLabel}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${getActivityTypeBadgeClass(type)}`}>
                            {typeLabel}
                          </span>
                          {sideLabel && (
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${getActivitySideBadgeClass(side)}`}>
                              {sideLabel}
                            </span>
                          )}
                          {item.outcome && (
                            <span className="px-2.5 py-1 rounded-lg text-xs font-medium border bg-slate-800/60 text-slate-200 border-slate-600/40">
                              {item.outcome}
                            </span>
                          )}
                          {txLabel && (
                            <span className="text-[10px] text-slate-500">
                              {t('traderDetail.activityTx')} {txLabel}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">{t('traderDetail.activityValue')}</span>
                          <span className="text-sm text-slate-200">{formatUSD(item.usdc_size)}</span>
                        </div>
                        {showPrice && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-slate-500">{t('traderDetail.activityPrice')}</span>
                            <span className="text-sm text-slate-200">
                              {item.price !== null && item.price !== undefined ? `$${formatActivityValue(item.price, 3)}` : '--'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">{t('traderDetail.activitySize')}</span>
                          <span className="text-sm text-slate-200">{formatActivityValue(item.size)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {activityHasMore && (
                <div className="flex justify-center mt-2">
                  <Button
                    variant="secondary"
                    size="medium"
                    onClick={handleLoadMoreActivity}
                    disabled={activityLoading}
                  >
                    {activityLoading ? t('common.loading') : t('traderDetail.activityLoadMore')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="relative z-10 flex flex-col items-center justify-center py-16 text-slate-500 gap-3">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
                <FiActivity className="text-3xl text-slate-600" />
              </div>
              <p className="text-sm text-slate-400">{t('traderDetail.activityEmpty')}</p>
              <p className="text-xs text-slate-600">{t('traderDetail.activityEmptyDesc')}</p>
              {activityHasMore && (
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={handleLoadMoreActivity}
                  disabled={activityLoading}
                >
                  {activityLoading ? t('common.loading') : t('traderDetail.activityLoadMore')}
                </Button>
              )}
            </div>
          )
        )}
      </div>

      </div>

      <Modal
        open={botRiskState.open}
        title={t('traderDetail.botRiskTitle')}
        subtitle={t('traderDetail.botRiskSubtitle')}
        onClose={closeBotRiskModal}
        maxWidthClass="max-w-[520px]"
      >
        <p className="text-slate-300 text-sm leading-relaxed mb-6">
          {t('traderDetail.botRiskDesc', { name: displayName, type: botRiskState.type || t('traderDetail.aiSuspectedRobot') })}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" size="medium" onClick={closeBotRiskModal}>
            {t('traderDetail.botRiskCancel')}
          </Button>
          <Button variant="primary" size="medium" onClick={handleBotRiskConfirm}>
            {t('traderDetail.botRiskConfirm')}
          </Button>
        </div>
      </Modal>

      <CopyTradeConfigModal
        open={copyConfigOpen}
        onClose={closeCopyConfigModal}
        trader={copyConfigTrader}
        onRequireMembership={onRequireMembership}
      />

      <CustomNameModal
        open={customNameOpen}
        onClose={() => setCustomNameOpen(false)}
        targetType="trader"
        targetId={address}
        currentName={profile.custom_name || ''}
        displayName={displayName}
        pushToast={pushToast}
        onSaved={handleCustomNameSaved}
      />
    </div>
  );
}
